<template>
  <img
    v-if="displaySrc && useDirectImage"
    :src="displaySrc"
    :alt="props.alt"
    :class="props.imageClass"
    :loading="props.loading"
    :fetchpriority="fetchPriority"
    decoding="async"
    @error="onImageError" />
  <NuxtImg
    v-else-if="displaySrc"
    :src="displaySrc"
    :alt="props.alt"
    :class="props.imageClass"
    :preset="props.preset"
    :format="imageFormat"
    :sizes="props.sizes"
    :loading="props.loading"
    :preload="nuxtPreload"
    decoding="async"
    @error="onImageError" />
</template>

<script lang="ts" setup>
import type { ComputedRef, PropType, Ref } from 'vue'

import { computed, ref, watch } from 'vue'
import { EMPTY_COVER_PLACEHOLDER } from '~/constants/imagePlaceholders'
import type { PrepeersImagePreset, PrepeersImageProps } from '~/types/PrepeersImage'
import { getNuxtImageFormatForSrc, isRemoteImageSrc } from '~/utils/imageSource'

/**
 * Définit les props du composant PrepeersImage.
 */
const props: PrepeersImageProps = defineProps({
  src: {
    type: String,
    default: '',
  },
  alt: {
    type: String,
    required: true,
  },
  preset: {
    type: String as PropType<PrepeersImagePreset>,
    required: true,
  },
  imageClass: {
    type: String,
    default: '',
  },
  sizes: {
    type: String,
    default: undefined,
  },
  loading: {
    type: String as PropType<'lazy' | 'eager'>,
    default: 'lazy',
  },
  preload: {
    type: [Boolean, Object] as PropType<boolean | { fetchPriority: 'auto' | 'high' | 'low' }>,
    default: false,
  },
  fallbackSrc: {
    type: String,
    default: EMPTY_COVER_PLACEHOLDER,
  },
})

const displaySrc: Ref<string> = ref<string>('')

const emit = defineEmits<{
  error: []
}>()

const useDirectImage: ComputedRef<boolean> = computed((): boolean => {
  const fromDisplay: string = displaySrc.value
  const fromProps: string = props.src?.trim() ?? ''
  return isRemoteImageSrc(fromDisplay) || isRemoteImageSrc(fromProps)
})

const nuxtPreload: ComputedRef<false | { fetchPriority: 'auto' | 'high' | 'low' }> = computed(
  () => {
    if (useDirectImage.value || !props.preload) return false
    if (props.preload === true) return { fetchPriority: 'high' }
    return props.preload
  },
)

const imageFormat: ComputedRef<'svg' | 'webp'> = computed((): 'svg' | 'webp' => {
  if (!displaySrc.value) return 'webp'
  return getNuxtImageFormatForSrc(displaySrc.value)
})

const fetchPriority: ComputedRef<'auto' | 'high' | 'low' | undefined> = computed(() => {
  if (!props.preload) return undefined
  if (props.preload === true) return 'high'
  return props.preload.fetchPriority
})

watch(
  (): string => props.src?.trim() ?? '',
  (nextSrc: string): void => {
    displaySrc.value = nextSrc
  },
  { immediate: true },
)

/**
 * Bascule sur l'image de secours en cas d'échec de chargement.
 * @returns {void}
 */
function onImageError(): void {
  const fallback: string = props.fallbackSrc?.trim() ?? ''
  if (fallback && displaySrc.value !== fallback) {
    displaySrc.value = fallback
    return
  }
  emit('error')
}
</script>
